/**
 * UpgradeModule -- Beautiful pricing page for AWENI freemium plans.
 *
 * Shows 4 plan cards (Free, Individual, Family, Supporter), donation section,
 * and FAQ. Accessible from Settings or contextual upgrade prompts.
 * Designed to feel generous and welcoming, not aggressive.
 */

import type { AweniContext } from '../../context/AweniContext';
import { FreemiumService, getFreemiumService, initFreemiumService } from '../../services/FreemiumService';
import type { Plan } from '../../services/FreemiumService';

// ---------- Plan Config ----------

interface PlanCard {
  plan: Plan;
  priceKey: string;
  price: string;
  period: string;
  features: string[];
  badge?: string;
  badgeColor?: string;
  cta: string;
  highlight?: boolean;
}

const PLAN_CARDS: PlanCard[] = [
  {
    plan: 'free',
    priceKey: 'plan_free',
    price: '$0',
    period: '',
    features: [
      'plan_free_feat_games',
      'plan_free_feat_reviews',
      'plan_free_feat_chat',
      'plan_free_feat_profile',
      'plan_free_feat_community',
      'plan_free_feat_basic',
    ],
    cta: 'plan_current',
  },
  {
    plan: 'individual',
    priceKey: 'plan_individual',
    price: '$4.99',
    period: '/mo',
    features: [
      'plan_ind_feat_unlimited',
      'plan_ind_feat_offline',
      'plan_ind_feat_ai',
      'plan_ind_feat_community_create',
      'plan_ind_feat_adfree',
    ],
    badge: 'plan_most_popular',
    badgeColor: '#3B82F6',
    cta: 'plan_start_trial',
    highlight: true,
  },
  {
    plan: 'family',
    priceKey: 'plan_family',
    price: '$9.99',
    period: '/mo',
    features: [
      'plan_fam_feat_everything_ind',
      'plan_fam_feat_profiles',
      'plan_fam_feat_parental',
      'plan_fam_feat_leaderboard',
      'plan_fam_feat_shared',
    ],
    badge: 'plan_best_value',
    badgeColor: '#0A0A0A',
    cta: 'plan_start_trial',
  },
  {
    plan: 'supporter',
    priceKey: 'plan_supporter',
    price: '$19.99',
    period: '/mo',
    features: [
      'plan_sup_feat_everything_fam',
      'plan_sup_feat_unlimited_profiles',
      'plan_sup_feat_sponsor',
      'plan_sup_feat_exclusive',
      'plan_sup_feat_beta',
    ],
    badge: 'plan_community_hero',
    badgeColor: '#F59E0B',
    cta: 'plan_start_trial',
  },
];

const DONATION_AMOUNTS = [5, 10, 25];

const FAQ_ITEMS = [
  'faq_cancel',
  'faq_data_safe',
  'faq_downgrade',
  'faq_refund',
];

// ---------- CSS ----------

const UPGRADE_CSS = `
.up-module { max-width: 720px; margin: 0 auto; padding: 0 16px 120px; }
.up-module h1 { font-size: 1.5rem; font-weight: 700; text-align: center; margin: 8px 0 4px; color: var(--text-primary); }
.up-subtitle { font-size: 0.9rem; color: var(--text-secondary); text-align: center; margin-bottom: 20px; max-width: 400px; margin-left: auto; margin-right: auto; }

/* Trial Banner */
.up-trial-banner { background: linear-gradient(135deg, var(--accent), #3B82F6); color: white; border-radius: 16px; padding: 16px 20px; margin-bottom: 20px; text-align: center; }
.up-trial-days { font-size: 1.5rem; font-weight: 700; }
.up-trial-text { font-size: 0.85rem; opacity: 0.9; }

/* Plan Cards -- horizontal scroll on mobile */
.up-plans { display: flex; gap: 12px; overflow-x: auto; padding-bottom: 16px; scroll-snap-type: x mandatory; scrollbar-width: none; }
.up-plans::-webkit-scrollbar { display: none; }

.up-plan { flex: 0 0 260px; scroll-snap-align: start; background: var(--card-bg); border-radius: 20px; padding: 24px 20px; box-shadow: var(--shadow-sm); border: 2px solid transparent; position: relative; transition: all 200ms ease; display: flex; flex-direction: column; }
.up-plan:hover { box-shadow: var(--shadow-md, 0 4px 12px rgba(0,0,0,0.1)); }
.up-plan.highlight { border-color: var(--accent); }
.up-plan.current { border-color: var(--text-secondary); opacity: 0.8; }

.up-badge { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); padding: 4px 14px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; color: white; white-space: nowrap; letter-spacing: 0.5px; text-transform: uppercase; }
.up-plan-name { font-size: 1.1rem; font-weight: 700; color: var(--text-primary); margin-top: 8px; }
.up-plan-price { margin: 8px 0 16px; }
.up-plan-price-amount { font-size: 2rem; font-weight: 800; color: var(--text-primary); }
.up-plan-price-period { font-size: 0.85rem; color: var(--text-secondary); }

.up-features { list-style: none; padding: 0; margin: 0 0 20px; flex: 1; }
.up-features li { font-size: 0.85rem; color: var(--text-primary); padding: 6px 0; display: flex; align-items: flex-start; gap: 8px; }
.up-features li::before { content: ''; display: inline-block; width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2310B981' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E"); background-size: contain; background-repeat: no-repeat; }

.up-cta { width: 100%; padding: 12px; border-radius: 12px; border: none; font-size: 0.95rem; font-weight: 600; cursor: pointer; transition: all 150ms ease; }
.up-cta-primary { background: var(--accent); color: var(--accent-text); }
.up-cta-primary:hover { opacity: 0.9; }
.up-cta-outline { background: transparent; border: 2px solid var(--border); color: var(--text-primary); }
.up-cta-outline:hover { border-color: var(--accent); color: var(--accent); }
.up-cta-current { background: var(--bg-secondary); color: var(--text-secondary); cursor: default; }

/* Donation Section */
.up-donate { background: var(--card-bg); border-radius: 20px; padding: 24px 20px; margin-top: 24px; text-align: center; box-shadow: var(--shadow-sm); }
.up-donate h2 { font-size: 1.15rem; font-weight: 700; color: var(--text-primary); margin-bottom: 4px; }
.up-donate-desc { font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 16px; max-width: 400px; margin-left: auto; margin-right: auto; line-height: 1.5; }
.up-donate-amounts { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-bottom: 12px; }
.up-donate-btn { padding: 10px 24px; border-radius: 12px; border: 2px solid var(--border); background: transparent; font-size: 0.95rem; font-weight: 600; color: var(--text-primary); cursor: pointer; transition: all 150ms ease; min-width: 70px; }
.up-donate-btn:hover { border-color: var(--accent); color: var(--accent); }
.up-donate-btn.active { border-color: var(--accent); background: rgba(10, 10, 10, 0.1); }
.up-donate-custom { display: flex; align-items: center; gap: 8px; justify-content: center; margin-top: 12px; }
.up-donate-input { width: 100px; padding: 8px 12px; border-radius: 10px; border: 1px solid var(--border); background: var(--bg-secondary); color: var(--text-primary); font-size: 0.9rem; text-align: center; }
.up-donate-submit { padding: 10px 20px; border-radius: 12px; border: none; background: var(--accent); color: var(--accent-text); font-size: 0.875rem; font-weight: 600; cursor: pointer; }

/* Sadaqah framing */
.up-sadaqah { font-size: 0.8rem; color: var(--text-secondary); font-style: italic; margin-top: 12px; line-height: 1.4; }

/* Trust Badges */
.up-trust { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
.up-trust-badge { display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: var(--text-secondary); }
.up-trust-badge svg { width: 16px; height: 16px; }

/* FAQ */
.up-faq { margin-top: 24px; }
.up-faq h2 { font-size: 1.15rem; font-weight: 700; color: var(--text-primary); margin-bottom: 12px; text-align: center; }
.up-faq-item { background: var(--card-bg); border-radius: 12px; margin-bottom: 8px; overflow: hidden; box-shadow: var(--shadow-sm); }
.up-faq-q { padding: 14px 16px; cursor: pointer; display: flex; justify-content: space-between; align-items: center; font-size: 0.9rem; font-weight: 600; color: var(--text-primary); user-select: none; }
.up-faq-chevron { width: 16px; height: 16px; color: var(--text-secondary); transition: transform 200ms ease; flex-shrink: 0; }
.up-faq-item.open .up-faq-chevron { transform: rotate(180deg); }
.up-faq-a { max-height: 0; overflow: hidden; transition: max-height 300ms ease; }
.up-faq-item.open .up-faq-a { max-height: 200px; }
.up-faq-a-inner { padding: 0 16px 14px; font-size: 0.85rem; color: var(--text-secondary); line-height: 1.5; }
`;

// ---------- Module ----------

export default class UpgradeModule {
  private _container: HTMLElement | null = null;
  private _ctx: AweniContext | null = null;
  private _freemium: FreemiumService | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this._container = container;
    this._ctx = ctx;

    // Inject styles
    if (!document.getElementById('up-module-styles')) {
      const style = document.createElement('style');
      style.id = 'up-module-styles';
      style.textContent = UPGRADE_CSS;
      document.head.appendChild(style);
    }

    this._freemium = getFreemiumService() || initFreemiumService(ctx.apiBase, () => ctx.auth.getToken());
    await this._freemium.init();

    this._render();
  }

  unmount(): void {
    if (this._container) this._container.innerHTML = '';
    this._container = null;
    this._ctx = null;
  }

  // ---------- Render ----------

  private _render(): void {
    if (!this._container || !this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);
    const state = this._freemium!.getState();

    this._container.innerHTML = `
      <div class="up-module">
        <h1>${t('upgrade_title')}</h1>
        <p class="up-subtitle">${t('upgrade_subtitle')}</p>

        ${state.trialDaysRemaining > 0 ? `
          <div class="up-trial-banner">
            <div class="up-trial-days">${state.trialDaysRemaining} ${t('upgrade_days_left')}</div>
            <div class="up-trial-text">${t('upgrade_trial_active')}</div>
          </div>
        ` : ''}

        <div class="up-plans">
          ${PLAN_CARDS.map((card) => this._renderPlanCard(card, t, state.plan)).join('')}
        </div>

        ${this._renderDonation(t)}
        ${this._renderTrustBadges(t)}
        ${this._renderFAQ(t)}
      </div>
    `;

    this._bindEvents();
  }

  private _renderPlanCard(card: PlanCard, t: (k: string) => string, currentPlan: Plan): string {
    const isCurrent = card.plan === currentPlan;
    const isUpgrade = !isCurrent && card.plan !== 'free';

    return `
      <div class="up-plan ${card.highlight ? 'highlight' : ''} ${isCurrent ? 'current' : ''}">
        ${card.badge ? `<div class="up-badge" style="background:${card.badgeColor || 'var(--accent)'}">${t(card.badge)}</div>` : ''}
        <div class="up-plan-name">${t(card.priceKey)}</div>
        <div class="up-plan-price">
          <span class="up-plan-price-amount">${card.price}</span>
          ${card.period ? `<span class="up-plan-price-period">${card.period}</span>` : ''}
        </div>
        <ul class="up-features">
          ${card.features.map((fk) => `<li>${t(fk)}</li>`).join('')}
        </ul>
        ${isCurrent
          ? `<button class="up-cta up-cta-current" disabled>${t('plan_current')}</button>`
          : isUpgrade
            ? `<button class="up-cta up-cta-primary" data-plan="${card.plan}">${t('plan_start_trial')}</button>`
            : `<button class="up-cta up-cta-outline" disabled>${t('plan_free')}</button>`
        }
      </div>
    `;
  }

  private _renderDonation(t: (k: string) => string): string {
    return `
      <div class="up-donate">
        <h2>${t('donate_title')}</h2>
        <p class="up-donate-desc">${t('donate_desc')}</p>
        <div class="up-donate-amounts">
          ${DONATION_AMOUNTS.map((amt) => `
            <button class="up-donate-btn" data-donate-amount="${amt}">$${amt}</button>
          `).join('')}
        </div>
        <div class="up-donate-custom">
          <span style="font-size:0.9rem;color:var(--text-secondary);">$</span>
          <input type="number" class="up-donate-input" id="up-custom-amount" min="1" placeholder="${t('donate_custom')}">
          <button class="up-donate-submit" id="up-donate-submit">${t('donate_give')}</button>
        </div>
        <p class="up-sadaqah">${t('donate_sadaqah')}</p>
      </div>
    `;
  }

  private _renderTrustBadges(t: (k: string) => string): string {
    return `
      <div class="up-trust">
        <div class="up-trust-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          ${t('trust_secure')}
        </div>
        <div class="up-trust-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          ${t('trust_cancel_anytime')}
        </div>
        <div class="up-trust-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          ${t('trust_money_back')}
        </div>
      </div>
    `;
  }

  private _renderFAQ(t: (k: string) => string): string {
    const chevron = `<svg class="up-faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>`;

    return `
      <div class="up-faq">
        <h2>${t('faq_title')}</h2>
        ${FAQ_ITEMS.map((key) => `
          <div class="up-faq-item">
            <div class="up-faq-q">${t(key + '_q')} ${chevron}</div>
            <div class="up-faq-a"><div class="up-faq-a-inner">${t(key + '_a')}</div></div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // ---------- Events ----------

  private _bindEvents(): void {
    if (!this._container || !this._freemium) return;

    // Plan CTA buttons
    this._container.querySelectorAll('[data-plan]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const plan = (btn as HTMLElement).dataset.plan as Plan;
        this._handlePlanSelect(plan);
      });
    });

    // Preset donation amounts
    this._container.querySelectorAll('[data-donate-amount]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const amount = Number((btn as HTMLElement).dataset.donateAmount);
        this._handleDonate(amount);
      });
    });

    // Custom donation
    this._container.querySelector('#up-donate-submit')?.addEventListener('click', () => {
      const input = this._container?.querySelector('#up-custom-amount') as HTMLInputElement;
      const amount = Number(input?.value);
      if (amount > 0) this._handleDonate(amount);
    });

    // FAQ accordion
    this._container.querySelectorAll('.up-faq-item').forEach((item) => {
      const q = item.querySelector('.up-faq-q');
      q?.addEventListener('click', () => item.classList.toggle('open'));
    });
  }

  private _handlePlanSelect(plan: Plan): void {
    if (!this._freemium) return;

    // D9 — guest gate: a tier-0 guest cannot enter a paid subscription
    // because the synthetic email isn't recoverable across devices, and
    // App Store / Google Play reject flows that take payment without an
    // account that can be restored. Redirect to /claim first; on success
    // the user is no longer a guest and can come back to upgrade.
    if (this._isGuest()) {
      this._showClaimGate('upgrade_guest_gate_title', 'upgrade_guest_gate_body', () => {
        if (this._ctx) this._ctx.navigate('/claim?return=/upgrade');
      });
      return;
    }

    // Redirect to Stripe checkout
    const checkoutUrl = this._freemium.getCheckoutUrl(plan);
    window.location.href = checkoutUrl;
  }

  private _handleDonate(amount: number): void {
    if (!this._freemium) return;

    // D9 — same guest gate for donations (they create a Stripe customer too).
    if (this._isGuest()) {
      this._showClaimGate('upgrade_guest_gate_title_donate', 'upgrade_guest_gate_body', () => {
        if (this._ctx) this._ctx.navigate('/claim?return=/upgrade');
      });
      return;
    }

    const donationUrl = this._freemium.getDonationUrl(amount);
    window.location.href = donationUrl;
  }

  private _isGuest(): boolean {
    return !!this._ctx?.auth?.user?.is_guest;
  }

  /** Light-weight in-place modal with a single primary CTA — keeps the
   *  user on the upgrade page so they can resume after claiming. */
  private _showClaimGate(titleKey: string, bodyKey: string, onPrimary: () => void): void {
    if (!this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);
    const isRtl = this._ctx.i18n.dir === 'rtl';
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:90;animation:askfabOverlayIn 200ms ease;';
    overlay.addEventListener('click', () => { overlay.remove(); sheet.remove(); });
    const sheet = document.createElement('div');
    sheet.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    sheet.style.cssText = 'position:fixed;inset-inline:0;bottom:0;background:var(--ln-bg-card,#fff);border-top-left-radius:20px;border-top-right-radius:20px;padding:18px 18px calc(18px + env(safe-area-inset-bottom));z-index:91;animation:askfabSheetIn 220ms cubic-bezier(0.2,0.7,0.2,1);box-shadow:0 -8px 24px rgba(0,0,0,0.18);color:var(--ln-text-primary,#1E293B);';
    sheet.innerHTML = `
      <div style="width:44px;height:4px;border-radius:999px;background:rgba(0,0,0,0.15);margin:0 auto 12px;"></div>
      <div style="font-size:18px;font-weight:800;margin-bottom:6px;">🔐 ${escHtml(t(titleKey) || 'Claim your account first')}</div>
      <div style="font-size:13px;line-height:1.55;color:var(--ln-text-secondary,#6b6657);margin-bottom:14px;">${escHtml(t(bodyKey) || 'Add your email and a password so this subscription stays linked to you across devices.')}</div>
      <button type="button" data-act="primary"
        style="display:block;width:100%;min-height:48px;padding:12px;border:none;border-radius:12px;background:var(--ln-accent,#0A0A0A);color:var(--ln-accent-text,#fff);font-weight:700;font-size:14px;cursor:pointer;">
        ${escHtml(t('upgrade_guest_gate_cta') || 'Claim my account')}
      </button>
      <button type="button" data-act="cancel"
        style="display:block;width:100%;margin-top:8px;min-height:44px;padding:10px;border:none;background:transparent;color:var(--ln-text-secondary,#6b6657);font-size:13px;cursor:pointer;">
        ${escHtml(t('common_cancel') || 'Cancel')}
      </button>
    `;
    sheet.querySelector('[data-act="primary"]')?.addEventListener('click', () => {
      overlay.remove();
      sheet.remove();
      onPrimary();
    });
    sheet.querySelector('[data-act="cancel"]')?.addEventListener('click', () => {
      overlay.remove();
      sheet.remove();
    });
    document.body.appendChild(overlay);
    document.body.appendChild(sheet);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
